import React, { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  Button,
  Spinner,
  Frame,
  Toast,
  BlockStack,
  Modal
} from '@shopify/polaris';
import { useAppContext } from '../context/AppContext';
import InClubDashboardBanner from '../components/InClubDashboardBanner';
import ConnectLanding from '../components/ConnectLanding';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

// Shopify Sales Channel requirement components
import PublishingSection from '../components/PublishingSection';
import CommissionSection from '../components/CommissionSection';
import ApprovalBanner from '../components/ApprovalBanner';
import HelpFooter from '../components/HelpFooter';
import TermsSection from '../components/TermsSection';
// Abandoned-cart push merchant surface HIDDEN for 2.0 (Michael, 2026-09-17):
// the checkout webhooks it depends on are not live (no `shopify app deploy`,
// no protected-customer-data approval), its copy promises website-cart
// coverage the sales-channel webhooks cannot deliver, and its "Grant access"
// banner requests a scope the app no longer declares. Re-enable together with
// follow-ups/abandoned-cart-merchant-surface.md.
// import AbandonedCartSection from '../components/AbandonedCartSection';

export default function Dashboard({
  isAuthenticated,
  authError,
  isInitializing,
  initError,
  connectionStatus,
  storeApproved,
  storeData,
  inclubData,
  onRetryConnection,
  onRetryAuth,
  onInstall,
  shop
}) {
  // Get context
  const { initializeApp } = useAppContext();

  // Get authenticated API hook
  const { authenticatedRequest } = useAuthenticatedApi(shop);

  // Check for return from InClub connection flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connectionStatus = urlParams.get('connection');
    const error = urlParams.get('error');

    if (connectionStatus === 'success') {
      console.log('✅ Returned from InClub with successful connection');
      setToastContent('✅ InClub connected successfully!');
      setShowToast(true);

      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('connection');
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl.toString());

      // Refresh app data to show connected state
      initializeApp(shop);
    } else if (connectionStatus === 'failed') {
      console.error('❌ InClub connection failed:', error);
      setToastContent(error || 'Failed to connect to InClub. Please try again.');
      setShowToast(true);
      setIsWaitingForConnection(false);

      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('connection');
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  // State for toast notifications
  const [toastContent, setToastContent] = useState('');
  const [showToast, setShowToast] = useState(false);

  // State for SSE connection waiting
  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);

  // State for Disconnect modal
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirmText, setDisconnectConfirmText] = useState('');

  const handleOpenInClub = () => {
    window.open(`${process.env.REACT_APP_INCLUB_URL}/inclub/${inclubData._id}`, '_blank');
  };

  const handleConnectToInClub = async () => {
    try {
      console.log('🔗 Starting InClub connection flow (redirect approach)...');
      setIsWaitingForConnection(true);

      // Create linking session
      console.log('📝 Creating linking session...');
      const sessionResponse = await authenticatedRequest('POST', `/../shopify-bridge-connector/create-linking-session`);

      if (!sessionResponse.success || !sessionResponse.sessionToken || !sessionResponse.shopDomain) {
        throw new Error('Failed to create linking session');
      }

      console.log('✅ Linking session created');

      // Build return URL for InClub to redirect back to
      const currentUrl = new URL(window.location.href);
      const returnUrl = encodeURIComponent(currentUrl.origin + currentUrl.pathname + '?shop=' + shop);

      // Full redirect to InClub with session token, shop domain, and return URL
      const inclubUrl = `${process.env.REACT_APP_INCLUB_URL}/integrations/shopify-bridge?token=${sessionResponse.sessionToken}&shopDomain=${encodeURIComponent(sessionResponse.shopDomain)}&returnUrl=${returnUrl}`;

      console.log('🔄 Redirecting to InClub for connection...');
      window.top.location.href = inclubUrl;

    } catch (err) {
      console.error('Failed to start connection flow:', err);
      // A 403 here means the store was un-approved since /init ran; re-init
      // so the landing flips back to "Contact us".
      if (err.code === 'STORE_NOT_APPROVED') {
        setToastContent(err.message);
        initializeApp(shop);
      } else {
        setToastContent('Failed to start connection. Please try again.');
      }
      setShowToast(true);
      setIsWaitingForConnection(false);
    }
  };

  // Disconnect InClub from store
  const handleDisconnect = async () => {
    // Validate confirmation text
    if (disconnectConfirmText.toLowerCase() !== 'disconnect') {
      setToastContent('Please type "disconnect" to confirm');
      setShowToast(true);
      return;
    }

    setDisconnecting(true);
    try {
      const data = await authenticatedRequest('DELETE', '/disconnect');

      if (data.success) {
        setToastContent('Disconnected successfully');
        setShowToast(true);
        setShowDisconnectModal(false);
        setDisconnectConfirmText('');
        // Refresh the app to show disconnected state
        await initializeApp(shop);
      } else {
        setToastContent('Failed to disconnect');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setToastContent('Failed to disconnect');
      setShowToast(true);
    } finally {
      setDisconnecting(false);
    }
  };

  // Authentication error state
  if (!isAuthenticated && authError) {
    return (
      <Page title="Authentication Error" narrowWidth>
        <Layout>
          <Layout.Section>
            <Banner status="critical" title="Authentication Failed">
              <BlockStack gap="4">
                <p>{authError}</p>
                <p><strong>Shop:</strong> {shop || 'Not provided'}</p>
                <p><strong>API Key:</strong> {process.env.REACT_APP_SHOPIFY_API_KEY ? 'Configured' : 'Missing'}</p>
              </BlockStack>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <Text variant="displayMedium" as="h1">
                    🔐 Authentication Required
                  </Text>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <Text variant="bodyLg" tone="subdued">
                    Unable to authenticate with Shopify. Please check your configuration and try again.
                  </Text>
                </div>

                <Button
                  primary
                  size="large"
                  onClick={onRetryAuth}
                >
                  Retry Authentication
                </Button>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Authentication loading state
  if (!isAuthenticated) {
    return (
      <Page title="Dashboard" narrowWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '20px' }}>
                  <Text variant="headingMd">Connecting to Shopify...</Text>
                  <Text variant="bodyMd" tone="subdued">Authenticating your store connection</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Initialization error state
  if (initError) {
    return (
      <Page title="Dashboard" narrowWidth>
        <Layout>
          <Layout.Section>
            <Banner status="critical" title="Initialization Failed">
              <BlockStack gap="4">
                <p>{initError}</p>
              </BlockStack>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <Text variant="displayMedium" as="h1">
                    ⚠️ Initialization Error
                  </Text>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <Text variant="bodyLg" tone="subdued">
                    Failed to initialize the connection to InClub. Please try again.
                  </Text>
                </div>

                <Button
                  primary
                  size="large"
                  onClick={onRetryConnection}
                >
                  Retry Initialization
                </Button>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Initialization loading state
  if (isInitializing) {
    return (
      <Page title="Dashboard" narrowWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '20px' }}>
                  <Text variant="headingMd">Initializing App...</Text>
                  <Text variant="bodyMd" tone="subdued">Setting up your InClub connection</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Connection status loading state - while checking connection
  if (!connectionStatus) {
    return (
      <Page title="Dashboard" narrowWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '20px' }}>
                  <Text variant="headingMd">Checking connection status...</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Success state - fully connected
  if (connectionStatus === 'connected' && storeData && inclubData) {
    return (
      <Frame>
        <Page
          title="Dashboard"
          narrowWidth
        >
          <Layout>
            {/* Sales Channel Status Banner */}
            <Layout.Section>
              <ApprovalBanner status={connectionStatus} />
            </Layout.Section>

            {/* Automatic-discount conflict: a store automatic discount Shopify
                won't combine with InClub's live spend-tier offers. Members still
                see the offers and Shopify applies the better discount at checkout
                (plan 2026-10-07-calm-cart WP2b — best deal wins). Tell the merchant
                where they manage discounts, with the server's notice per discount
                (storeDiscountNotice). `pausedStackingOffers` is the server's count of
                the live offers running beside the sale — the name predates WP2b. */}
            {inclubData.automaticDiscountConflict?.active && inclubData.pausedStackingOffers > 0 && (
              <Layout.Section>
                <Banner
                  status="warning"
                  title={`Your automatic discount runs beside ${inclubData.pausedStackingOffers > 1 ? `${inclubData.pausedStackingOffers} InClub offers` : 'an InClub offer'} — Shopify applies the better one at checkout`}
                >
                  {inclubData.automaticDiscountConflict.notices?.length ? (
                    inclubData.automaticDiscountConflict.notices.map((notice) => (
                      <Text as="p" key={notice}>{notice}</Text>
                    ))
                  ) : (
                    <Text as="p">
                      {`Your Shopify discount "${inclubData.automaticDiscountConflict.titles?.[0] || 'your automatic discount'}" doesn't combine with InClub's spend-tier offers. Members still see this deal; Shopify applies the better of the two at checkout.`}
                    </Text>
                  )}
                </Banner>
              </Layout.Section>
            )}

            {/* Club Banner */}
            <Layout.Section>
              <InClubDashboardBanner inclubData={inclubData} />
            </Layout.Section>

            {/* Go to InClub Button */}
            <Layout.Section>
              <Card>
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text variant="headingLg" as="h3">Manage Your Community</Text>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <Text variant="bodyMd" tone="subdued">
                      Visit the InClub dashboard to manage members, content, and settings
                    </Text>
                  </div>
                  <Button
                    primary
                    size="large"
                    onClick={handleOpenInClub}
                  >
                    Go to InClub
                  </Button>
                </div>
              </Card>
            </Layout.Section>

            {/* Abandoned Cart Push - opt-in prompt, set-up card, settings.
                Hidden for 2.0 — see the import comment at the top of this file
                and follow-ups/abandoned-cart-merchant-surface.md.
            <AbandonedCartSection
              inclubId={inclubData._id}
              authenticatedRequest={authenticatedRequest}
            />
            */}

            {/* Product Publishing Section - Sales Channel Requirement */}
            <PublishingSection
              shopDomain={shop}
              inclubId={inclubData._id}
              authenticatedRequest={authenticatedRequest}
            />

            {/* Commission Section - Sales Channel Requirement */}
            <CommissionSection />

            {/* Terms Section - Sales Channel Requirement */}
            <TermsSection />

            {/* Disconnect InClub from Store */}
            <Layout.Section>
              <Card>
                <div style={{ padding: '20px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text variant="headingLg" as="h3">Disconnect InClub</Text>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <Text variant="bodyMd" tone="subdued">
                      This will disconnect your InClub from this Shopify store. This action cannot be undone.
                    </Text>
                  </div>
                  <Button
                    destructive
                    onClick={() => setShowDisconnectModal(true)}
                  >
                    Disconnect
                  </Button>
                </div>
              </Card>
            </Layout.Section>

            {/* Help Footer - Sales Channel Requirement */}
            <Layout.Section>
              <HelpFooter />
            </Layout.Section>
        </Layout>
      </Page>

      {/* Disconnect Confirmation Modal */}
      <Modal
        open={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setDisconnectConfirmText('');
        }}
        title="Disconnect InClub from Store"
        primaryAction={{
          content: 'Disconnect',
          destructive: true,
          loading: disconnecting,
          disabled: disconnectConfirmText.toLowerCase() !== 'disconnect',
          onAction: handleDisconnect
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDisconnectModal(false);
              setDisconnectConfirmText('');
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="4">
            <Text variant="bodyMd">
              Are you sure you want to disconnect your InClub from this Shopify store?
            </Text>
            <Text variant="bodyMd" tone="subdued">
              This will:
            </Text>
            <ul style={{ paddingLeft: '20px' }}>
              <li>Remove the connection between your store and InClub</li>
              <li>Remove the Shopify domain from your InClub</li>
            </ul>
            <Text variant="bodyMd" tone="critical" fontWeight="semibold">
              Note: This will NOT delete your InClub, just the connection between Shopify and InClub.
            </Text>

            <div style={{ marginTop: '16px' }}>
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                Type <strong>disconnect</strong> to confirm:
              </Text>
              <div style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  value={disconnectConfirmText}
                  onChange={(e) => setDisconnectConfirmText(e.target.value)}
                  placeholder="Type disconnect here"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #c4cdd5',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  autoComplete="off"
                />
              </div>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Toast */}
      {showToast && (
        <Toast
          content={toastContent}
          onDismiss={() => setShowToast(false)}
        />
      )}
    </Frame>
    );
  }

  // Not connected — landing page detailing what InClub does on connect
  return (
    <Page title="Dashboard" narrowWidth>
      <Layout>
        {/* Waiting for connection banner */}
        {isWaitingForConnection && (
          <Layout.Section>
            <Banner tone="info" title="Waiting for InClub Connection">
              <BlockStack gap="4">
                <p>Please complete the InClub setup in the tab that opened.</p>
                <p>This page will automatically update when the connection is complete.</p>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        <ConnectLanding
          storeApproved={storeApproved}
          onConnect={handleConnectToInClub}
          isWaitingForConnection={isWaitingForConnection}
        />

        {/* Debug Information */}
        {(storeData || inclubData) && (
          <Layout.Section>
            <Card title="Connection Debug Info">
              <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                <Text variant="headingXs">Status: {connectionStatus}</Text><br />
                <Text variant="headingXs">Store Data: {storeData ? '✅ Available' : '❌ Missing'}</Text><br />
                <Text variant="headingXs">InClub Data: {inclubData ? '✅ Available' : '❌ Missing'}</Text>
              </div>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}